import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { useQueryClient } from "@tanstack/react-query";

import type { LandingPageAttributes } from "hooks/api/use-touchpoint-versions";

import { useBuilderContent } from "hooks/api/use-touchpoint-versions";
import { campaignKeys } from "hooks/api/query-keys";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import {
  CustomInput,
  CustomSelect,
  CustomTextarea,
} from "components/partials/custom-form-inputs/custom-form-inputs";

import { CustomFormField } from "utilities/forms";
import { getLPBuilderContentSettingsValidationSchema } from "containers/admin/clients/touchpoint/components/lp-builder/lp-builder-content-settings-tab/lp-builder-content-settings-tab.constants";

interface LPBuilderContentSettingsTabProps {
  previewMode: boolean;
  formFields: CustomFormField[];
  onSave: (field: Record<string, unknown>) => Promise<void>;
  setIsSaved: (isSaved: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  isTemplate: boolean;
}

const LPBuilderContentSettingsTab = ({
  previewMode = false,
  formFields,
  onSave,
  setIsSaved,
  setIsSaving,
  isTemplate,
}: LPBuilderContentSettingsTabProps) => {
  const initialAttributes = useBuilderContent() as LandingPageAttributes;
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const queryClient = useQueryClient();

  const [hasChanges, setHasChanges] = useState(false);
  const lastSavedValuesRef = useRef<Record<string, unknown> | null>(null);

  const validationSchema = useMemo(
    () => getLPBuilderContentSettingsValidationSchema(formFields),
    [formFields],
  );

  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    watch,
    trigger,
    clearErrors,
  } = useForm<Record<string, unknown>>({
    mode: "onChange",
    resolver: validationSchema,
  });

  const hasInitializedRef = useRef(false);

  useEffect(
    function setInitialValues() {
      if (hasInitializedRef.current) return;
      hasInitializedRef.current = true;

      formFields.forEach((field) => {
        const fieldName = field.name as keyof LandingPageAttributes;
        const initialValue = initialAttributes[fieldName] || field.defaultValue;
        setValue(fieldName, initialValue ? `${initialValue}` : "");
      });

      const redirectDestination = initialAttributes.redirectDestination;
      const redirectDestinationType = initialAttributes.redirectDestinationType;
      const selectValue = "redirectDestinationSelect";

      switch (redirectDestinationType) {
        case "URL":
          setValue(selectValue, "custom-url");
          break;
        case "TOUCHPOINT":
          setValue(selectValue, redirectDestination);
          break;
        case "NONE":
        default:
          setValue(selectValue, "no-redirect");
          break;
      }

      // Trigger validation to show errors for invalid initial values (e.g., blank required fields)
      trigger().then((isValid) => {
        // Create a snapshot of the initial values
        lastSavedValuesRef.current = getValues();

        const formData = getValues();
        const { redirectDestinationSelect: _select, ...dataToSubmit } = formData;
        setIsSaving(true);
        onSave({
          ...dataToSubmit,
          publishable: isTemplate ? false : isValid,
          disablePrefill: true,
          id: initialAttributes.id,
          name: initialAttributes.name,
          touchpointId: initialAttributes.touchpointId,
        })
          .then(() => setIsSaved(true))
          .catch((e) => {
            console.error("Failed to save on initial load", e);
            setIsSaved(false);
          })
          .finally(() => setIsSaving(false));
      });
    },
    // eslint-disable-next-line
    [formFields],
  );

  const watchValues = watch();

  const urlValue = watch("url");

  useEffect(() => {
    // Prevent a user from typing anything other than letters into the Page URL field.
    if (!urlValue) return;

    const sanitizedValue = (urlValue as string).replace(/[^a-zA-Z-_]/g, "");
    if (urlValue !== sanitizedValue) {
      setValue("url", sanitizedValue);
    }
  }, [urlValue, setValue]);

  // Refetch campaign when user types in URL so CDN subdirectory updated in another tab is picked up
  const refetchCampaignTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refetchCampaign = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: campaignKeys.detail(currentClient.id, currentCampaign.id),
    });
    queryClient.invalidateQueries({ queryKey: campaignKeys.lists() });
  }, [queryClient, currentClient.id, currentCampaign.id]);

  useEffect(() => {
    // Only refetch when user has interacted with the URL field (to pick up CDN set in another tab)
    if (urlValue === undefined || urlValue === null) return;
    if (typeof urlValue === "string" && !urlValue.trim()) return;

    refetchCampaignTimeoutRef.current = setTimeout(refetchCampaign, 400);

    return () => {
      if (refetchCampaignTimeoutRef.current) {
        clearTimeout(refetchCampaignTimeoutRef.current);
        refetchCampaignTimeoutRef.current = null;
      }
    };
  }, [urlValue, refetchCampaign]);

  useEffect(() => {
    trigger("url");
  }, [currentCampaign.cdnSubdirectoryName, trigger]);

  // Handle redirect destination select changes
  const redirectDestinationSelectValue = watch("redirectDestinationSelect");

  useEffect(() => {
    if (!redirectDestinationSelectValue) return;

    switch (redirectDestinationSelectValue) {
      case "custom-url":
        trigger("redirectDestination");
        setValue("redirectDestinationType", "URL");
        break;

      case "no-redirect":
        setValue("redirectDestination", "no-redirect");
        clearErrors("redirectDestination");
        setValue("redirectDestinationType", "NONE");
        break;

      default:
        // Any other value is treated as a touchpoint
        setValue("redirectDestination", redirectDestinationSelectValue);
        clearErrors("redirectDestination");
        setValue("redirectDestinationType", "TOUCHPOINT");
    }
  }, [clearErrors, redirectDestinationSelectValue, setValue, trigger]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const formData = getValues();
      const hasErrors = !!Object.keys(errors).length;
      // `redirectDestinationSelect` is used to set redirectDestination and redirectDestinationType
      const { redirectDestinationSelect: _, ...dataToSubmit } = formData;
      await onSave({
        ...dataToSubmit,
        publishable: isTemplate ? false : !hasErrors,
        disablePrefill: true,
        id: initialAttributes.id,
        name: initialAttributes.name,
        touchpointId: initialAttributes.touchpointId,
      });

      // Refresh saved values snapshot
      lastSavedValuesRef.current = formData;
      setIsSaved(true);
    } catch (error) {
      console.error("Failed to save", error);
      setIsSaved(false);
    } finally {
      setIsSaving(false);
    }
  };

  /** Saves on blur if field value has been changed */
  const handleFieldBlur = async () => {
    if (hasChanges) {
      try {
        await handleSave();
      } catch (e) {
        console.error("Error when save onBlur", e);
      }
    }
  };

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    /** Autosave after 2 second debounce if a change has been made */
    function autosave() {
      if (hasChanges) {
        saveTimeoutRef.current = setTimeout(() => {
          handleSave();
        }, 2000);
      }

      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasChanges],
  );

  useEffect(() => {
    // Compare current values with snapshot to determine if there are changes
    const currentValues = getValues();
    const hasActualChanges =
      !isSubmitting &&
      lastSavedValuesRef.current !== null &&
      JSON.stringify(currentValues) !== JSON.stringify(lastSavedValuesRef.current);

    setHasChanges(hasActualChanges);
  }, [watchValues, isSubmitting, getValues]);

  /** Create dynamic string as user types, with trailing forward slash */
  const getUrlEyebrow = () => {
    const domain = currentClient.apolloDomainUrl?.replace(/\/+$/, "");
    const cdn = currentCampaign.cdnSubdirectoryName?.trim() || "Not set";
    return `${[domain, cdn].filter(Boolean).join("/")}/${urlValue}`;
  };

  return (
    <form
      className="flex flex-col max-w-[37.5rem] w-full mx-auto space-y-4"
      onSubmit={() => handleSave()}
      noValidate
    >
      {formFields.map((field) => {
        if (field.component === "hidden" || (field.condition && !field.condition(watchValues))) {
          return null;
        }

        const dynamicUrlEyebrow = field.name === "url" ? getUrlEyebrow() : null;

        return (
          <div key={`${field.name}-${field.component}`}>
            <Controller
              control={control}
              name={field.name}
              render={({ field: { onChange, onBlur, value } }) => {
                const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  const sanitized = e.target.value.replace(/[^a-zA-Z-_]/g, "");
                  onChange(sanitized);
                };

                const fieldError = errors[field.name];
                const renderedError =
                  fieldError?.message && field.validations?.renderError
                    ? field.validations.renderError(fieldError.message as string)
                    : fieldError;

                const commonProps = {
                  disabled: previewMode,
                  error: renderedError,
                  eyebrow: dynamicUrlEyebrow ?? field.eyebrow,
                  helperText: field.helperText,
                  label: field.label,
                  name: field.name,
                  onChange,
                  onBlur: () => {
                    onBlur();
                    handleFieldBlur();
                  },
                  placeholder: field.placeholder,
                  required: !!field.validations?.isRequired,
                  value: String(value ?? ""),
                };

                switch (field.component) {
                  case "select":
                    return <CustomSelect options={field.options} {...commonProps} />;
                  case "textarea":
                    return <CustomTextarea {...commonProps} />;

                  default:
                    return (
                      <CustomInput
                        type="text"
                        addonStart={field.addonStart}
                        addonEnd={field.addonEnd}
                        {...commonProps}
                        {...(field.name === "url" && { onChange: handleUrlChange })}
                      />
                    );
                }
              }}
            />
          </div>
        );
      })}
    </form>
  );
};

export default LPBuilderContentSettingsTab;
